import { useMemo } from "react";
import { ShopifyProduct } from "@/lib/shopify";

// Shipping cutoff: 14:00 Swedish time (CET/CEST)
const SHIPPING_CUTOFF_HOUR = 14;

export interface FomoSignal {
  type: "demand" | "scarcity" | "time" | "social";
  message: string;
  icon?: string;
}

interface FomoConfig {
  isBestseller?: boolean;
  isFeatured?: boolean;
  isPinned?: boolean; // e.g., Bubble Hash
  isNew?: boolean;
  productIndex?: number; // position in list (top 4 = popular)
}

/**
 * Determines if it's before the shipping cutoff for same-day dispatch
 */
function isBeforeShippingCutoff(): boolean {
  const now = new Date();
  // Convert to Swedish timezone (approximate - uses local time)
  const swedishHour = now.getHours();
  const dayOfWeek = now.getDay();
  
  // Weekdays only (Mon-Fri = 1-5)
  const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
  
  return isWeekday && swedishHour < SHIPPING_CUTOFF_HOUR;
}

/**
 * Check if product is in stock based on variant availability
 */
function checkIsInStock(product: ShopifyProduct): boolean {
  return product.node.variants.edges.some(v => v.node.availableForSale);
}

/**
 * Check if product has limited stock (most variants unavailable)
 * Since Storefront API doesn't expose quantity, we infer from variant availability
 */
function hasLimitedStock(product: ShopifyProduct): boolean {
  const variants = product.node.variants.edges;
  if (variants.length <= 1) return false; // Can't determine for single-variant products
  
  const availableCount = variants.filter(v => v.node.availableForSale).length;
  const unavailableCount = variants.length - availableCount;
  
  // If more than half the variants are sold out, consider it limited
  return unavailableCount > availableCount && availableCount > 0;
}

/**
 * Returns truthful FOMO signals based on product data and config
 */
export function useFomoSignals(
  product: ShopifyProduct | null | undefined,
  config: FomoConfig = {}
): FomoSignal[] {
  return useMemo(() => {
    if (!product) return [];
    
    const signals: FomoSignal[] = [];
    const isInStock = checkIsInStock(product);
    
    // Don't show any FOMO for out-of-stock products
    if (!isInStock) return [];
    
    const { isBestseller, isFeatured, isPinned, isNew, productIndex } = config;
    
    // 1. DEMAND-BASED FOMO (for popular products)
    // Only show if product is demonstrably popular
    const isPopular = isBestseller || isFeatured || isPinned || (productIndex !== undefined && productIndex < 4);
    
    if (isPinned) {
      // Strongest signal for pinned/hottest products
      signals.push({
        type: "demand",
        message: "Hög efterfrågan",
        icon: "🔥"
      });
    } else if (isBestseller || isFeatured) {
      signals.push({
        type: "demand",
        message: "Populär just nu",
        icon: "🔥"
      });
    } else if (productIndex !== undefined && productIndex < 4) {
      // Top 4 products in a list
      signals.push({
        type: "social",
        message: "En av våra mest valda",
        icon: "✔"
      });
    }
    
    // 2. SCARCITY-BASED FOMO (inventory-aware)
    if (hasLimitedStock(product)) {
      signals.push({
        type: "scarcity",
        message: "Begränsat lager",
        icon: "⏳"
      });
    }
    
    // 3. TIME-BASED FOMO (shipping cutoff)
    // Only show for popular or featured products to avoid clutter
    if (isPopular && isBeforeShippingCutoff()) {
      signals.push({
        type: "time",
        message: "Beställ nu – skickas idag",
        icon: "⚡"
      });
    }
    
    // 4. SOCIAL PROOF (for bestsellers only)
    if (isBestseller && !isPinned) {
      signals.push({
        type: "social",
        message: "Bästsäljare i kategorin",
        icon: "⭐"
      });
    }
    
    // Limit to max 2 signals to avoid clutter (mobile-first)
    return signals.slice(0, 2);
  }, [product, config.isBestseller, config.isFeatured, config.isPinned, config.isNew, config.productIndex]);
}

/**
 * Get a single primary FOMO message for compact displays (cards)
 */
export function usePrimaryFomoSignal(
  product: ShopifyProduct | null | undefined,
  config: FomoConfig = {}
): FomoSignal | null {
  const signals = useFomoSignals(product, config);
  return signals[0] || null;
}

/**
 * Check if shipping cutoff is active (for display purposes)
 */
export function useShippingCutoff(): { isBeforeCutoff: boolean; message: string } {
  const isBeforeCutoff = isBeforeShippingCutoff();
  
  return {
    isBeforeCutoff,
    message: isBeforeCutoff 
      ? "Beställ idag – skickas samma dag"
      : "Beställ nu – skickas nästa vardag"
  };
}
